<template>
  <div class="page-container">
    <main class="main-content">
      <div class="page-inner-box">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
          <el-breadcrumb-item>所有课程</el-breadcrumb-item>
        </el-breadcrumb>

        <div class="toolbar">
          <el-input
            v-model="query.keyword"
            size="small"
            clearable
            placeholder="搜索课程"
            class="search-input"
            @keyup.enter.native="handleSearch"
          />
          <el-button type="primary" size="small" @click="handleSearch">搜索</el-button>
        </div>

        <!-- 班级推荐区域 -->
        <div v-if="classList.length > 0" class="class-section">
          <div class="section-header">
            <h3 class="section-title">推荐班级</h3>
            <el-button type="primary" link @click="goClassList">查看更多</el-button>
          </div>
          <div class="class-grid">
            <el-card v-for="cls in classList" :key="cls.id" class="class-card ui-card" shadow="always" @click.native="goClassDetail(cls)">
              <div class="class-thumb">
                <img :src="cls.coverImage" alt="cover" />
                <div class="class-badge">班级</div>
              </div>
              <div class="class-info">
                <div class="class-title">{{ cls.className }}</div>
                <div class="class-subtitle">{{ cls.subtitle }}</div>
                <div class="class-meta">
                  <span class="class-price">¥{{ (cls.price || 0).toFixed(2) }}</span>
                  <span class="class-stats">{{ cls.courseCount || 0 }}门课程 · {{ cls.salesCount || 0 }}人已购</span>
                </div>
              </div>
            </el-card>
          </div>
        </div>

        <div class="list-body" v-loading="loading">
          <div v-if="courses.length > 0" class="grid">
            <el-card v-for="course in courses" :key="course.id" class="card ui-card" shadow="always" @click.native="goDetail(course)">
              <div class="thumb">
                <img :src="course.coverImage" alt="cover" />
              </div>
              <div class="title">{{ course.courseName }}</div>
              <div class="subtitle">{{ course.subtitle }}</div>
              <div class="rate">
                <el-rate
                  :value="course.starRating"
                  :max="5"
                  :colors="['#f59e0b', '#f59e0b', '#f59e0b']"
                  disabled
                />
              </div>
              <div class="meta">
                <span class="price">{{ course.priceText }}</span>
                <span class="views">{{ course.viewCount }}人已学</span>
              </div>
            </el-card>
          </div>

          <el-empty v-else-if="!loading" description="暂无课程" />
        </div>

        <div v-if="total > query.pageSize" class="pager">
          <el-pagination
            :current-page.sync="query.pageNum"
            :page-size="query.pageSize"
            layout="prev, pager, next"
            :total="total"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { getCourseList } from '@/api/courseApi'
import { getClassList } from '@/api/classApi'

export default {
  name: 'CourseList',
  data() {
    return {
      loading: false,
      total: 0,
      courses: [],
      classList: [],
      query: {
        keyword: '',
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  created() {
    const keyword = String((this.$route && this.$route.query && this.$route.query.keyword) || '').trim()
    if (keyword) this.query.keyword = keyword
    this.fetchCourses()
    this.fetchClasses()
  },
  watch: {
    '$route.query.keyword'(next) {
      const keyword = String(next || '').trim()
      if (keyword === this.query.keyword) return
      this.query.keyword = keyword
      this.query.pageNum = 1
      this.fetchCourses()
    }
  },
  methods: {
    extractListAndTotal(data) {
      if (!data) return { list: [], total: 0 }
      const list = Array.isArray(data.list)
        ? data.list
        : (Array.isArray(data.records) ? data.records : (Array.isArray(data.items) ? data.items : []))
      const total = data.total == null ? list.length : Number(data.total)
      return { list, total: Number.isNaN(total) ? list.length : total }
    },
    normalizeCourse(item, index) {
      const id = (item && (item.id ?? item.courseId ?? item.course_id)) ?? index
      const courseName = (item && (item.courseName ?? item.title ?? item.name)) || ''
      const subtitle = (item && (item.subtitle ?? item.desc ?? item.description)) || ''
      const coverImage = (item && (item.coverImage ?? item.cover ?? item.image)) || ''
      const viewCountRaw = item && (item.viewCount ?? item.studentCount ?? item.students)
      const viewCount = viewCountRaw == null ? 0 : Number(viewCountRaw)
      const ratingRaw = item && (item.starRating ?? item.star_rating ?? item.rating)
      const ratingNum = ratingRaw == null ? 0 : Number(ratingRaw)
      const starRating = Number.isFinite(ratingNum) ? Math.min(5, Math.max(0, ratingNum)) : 0

      const discountPrice = item && (item.discountPrice ?? item.discount_price)
      const currentPrice = item && (item.currentPrice ?? item.current_price)
      const salePrice = item && (item.salePrice ?? item.sale_price)
      const priceNumberRaw = discountPrice ?? currentPrice ?? salePrice ?? 0
      const priceNumber = priceNumberRaw == null ? 0 : Number(priceNumberRaw)
      const priceText = priceNumber > 0 ? `¥${priceNumber}` : '免费'

      return {
        id,
        courseName,
        subtitle,
        coverImage,
        viewCount: Number.isNaN(viewCount) ? 0 : viewCount,
        starRating,
        priceText,
        raw: item
      }
    },
    async fetchCourses() {
      if (this.loading) return
      this.loading = true
      try {
        const params = {
          keyword: this.query.keyword || undefined,
          pageNum: this.query.pageNum,
          pageSize: this.query.pageSize
        }
        const res = await getCourseList(params)
        const { list, total } = this.extractListAndTotal(res && res.data)
        this.courses = list.map((item, index) => this.normalizeCourse(item, index))
        this.total = total
      } catch (err) {
        this.courses = []
        this.total = 0
        this.$message.error((err && err.message) || '课程列表加载失败')
      } finally {
        this.loading = false
      }
    },
    async fetchClasses() {
      try {
        const res = await getClassList({ pageNum: 1, pageSize: 4 })
        if (res && res.code === 200 && res.data) {
          this.classList = res.data.list || []
        }
      } catch (err) {
        console.error('获取班级列表失败', err)
      }
    },
    goClassList() {
      this.$router.push({ name: 'classList' })
    },
    goClassDetail(cls) {
      const id = cls && cls.id
      if (id == null || id === '') return
      this.$router.push({ name: 'classDetail', params: { id } })
    },
    handleSearch() {
      const keyword = String(this.query.keyword || '').trim()
      if (keyword === String((this.$route && this.$route.query && this.$route.query.keyword) || '').trim()) {
        this.query.pageNum = 1
        this.fetchCourses()
        return
      }
      this.$router.push({ name: 'courseList', query: { ...(keyword ? { keyword } : {}) } })
    },
    handlePageChange(page) {
      this.query.pageNum = page
      this.fetchCourses()
    },
    goDetail(course) {
      const id = course && course.id
      if (id == null || id === '') return
      this.$router.push({ name: 'course', params: { id } })
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; background-color: #f5f7fa; }
.main-content { flex: 1; padding-top: 20px; }

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0 20px;
}

.search-input { width: 360px; }

.list-body { min-height: 240px; }

.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.card {
  cursor: pointer;
}

.thumb {
  width: 100%;
  height: 140px;
  border-radius: 4px;
  overflow: hidden;
  background: #f2f2f2;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.title {
  margin-top: 10px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 34px;
}

.rate {
  margin-top: 6px;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.price {
  color: #ff4433;
  font-weight: 600;
}

.views {
  font-size: 12px;
  color: #999;
}

/* 班级推荐区域样式 */
.class-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.class-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.class-card {
  cursor: pointer;
  transition: transform 0.3s;
}

.class-card:hover {
  transform: translateY(-4px);
}

.class-thumb {
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  background: #f2f2f2;
}

.class-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.class-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.class-info {
  padding: 12px 0;
}

.class-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.class-price {
  color: #ff4433;
  font-weight: 600;
  font-size: 16px;
}

.class-stats {
  font-size: 12px;
  color: #999;
}

.pager {
  display: flex;
  justify-content: center;
  margin: 24px 0 10px;
}
</style>
